// WallMist — the fog that makes the landlocked mountain wall read as a real
// boundary (docs/sea-design.md A5). Render-only, two draw calls total:
//   1. ONE mesh: a mist curtain strip along every outer board edge (alpha
//      gradient up the wall's face and a skirt thinning away beyond it);
//   2. ONE InstancedMesh of <= WISP_CAP camera-facing puffs anchored on wall
//      tiles, drifting along the wall (all motion in the shader).
// Both use module-level shared materials. The existing GroundFog already
// anchors wisps on level >= 8 mountains on misty days; this adds the
// always-on wall mist on top. Honours prefers-reduced-motion and the
// particleEffects setting like Waterfalls (clock frozen; puffs off).

import { useFrame } from '@react-three/fiber';
import { useEffect, useMemo, useRef } from 'react';
import * as THREE from 'three';
import { useSettings } from '../../settings/useSettings.js';
import { dayNightState, nightFactor } from '../DayNightLighting.js';
import { prefersReducedMotion } from '../Waterfalls.js';
import { buildCurtainGeometry } from './seaGeometry.js';
import type { BoundaryEdge } from './seaTiles.js';
import { CURTAIN_FRAG, CURTAIN_VERT, WISP_FRAG, WISP_VERT } from './wallMistShaders.js';
import { wispAnchors } from './wispAnchors.js';

/** Curtain base height: just above the lowest wall tile surface. */
const CURTAIN_BASE_Y = 0.25;

const uTime = { value: 0 };
const uLight = { value: 1 };
const uColor = { value: new THREE.Color('#dde7ea') };

const curtainMaterial = new THREE.ShaderMaterial({
  uniforms: { uTime, uLight, uColor },
  vertexShader: CURTAIN_VERT,
  fragmentShader: CURTAIN_FRAG,
  transparent: true,
  depthWrite: false,
  side: THREE.DoubleSide,
});
curtainMaterial.customProgramCacheKey = () => 'wall-mist-curtain';

const wispMaterial = new THREE.ShaderMaterial({
  uniforms: { uTime, uLight, uColor },
  vertexShader: WISP_VERT,
  fragmentShader: WISP_FRAG,
  transparent: true,
  depthWrite: false,
});
wispMaterial.customProgramCacheKey = () => 'wall-mist-wisp';
const wispGeo = new THREE.PlaneGeometry(1, 1);

export function WallMist({
  edges,
  wall,
  signature,
  seed,
}: {
  readonly edges: ReadonlyArray<BoundaryEdge>;
  /** key -> wall row, from wallRows(). */
  readonly wall: ReadonlyMap<string, number>;
  readonly signature: string;
  readonly seed: number;
}) {
  const particleEffects = useSettings((s) => s.particleEffects);
  const reduced = useMemo(prefersReducedMotion, []);
  const wispRef = useRef<THREE.InstancedMesh>(null);

  // biome-ignore lint/correctness/useExhaustiveDependencies: keyed on the terrain signature (RCA #139/#140)
  const curtain = useMemo(() => buildCurtainGeometry(edges, CURTAIN_BASE_Y), [signature]);
  // biome-ignore lint/correctness/useExhaustiveDependencies: keyed on the terrain signature
  const anchors = useMemo(() => wispAnchors(wall, seed), [signature, seed]);
  useEffect(() => () => curtain.dispose(), [curtain]);

  useEffect(() => {
    const mesh = wispRef.current;
    if (!mesh) return;
    const o = new THREE.Object3D();
    anchors.forEach((a, i) => {
      o.position.set(a.x, a.y, a.z);
      o.scale.set(a.size, a.size, 1);
      o.updateMatrix();
      mesh.setMatrixAt(i, o.matrix);
    });
    mesh.count = anchors.length;
    mesh.instanceMatrix.needsUpdate = true;
  }, [anchors, particleEffects]);

  useFrame((_, delta) => {
    uLight.value = 1 - 0.6 * nightFactor(dayNightState.sunHeight);
    if (!reduced && particleEffects) uTime.value += Math.min(delta, 0.1);
  });

  return (
    <group name="wall-mist">
      <mesh
        name="wall-mist-curtain"
        geometry={curtain}
        material={curtainMaterial}
        frustumCulled={false}
        raycast={() => null}
        renderOrder={6}
      />
      {particleEffects && anchors.length > 0 && (
        <instancedMesh
          key={anchors.length}
          ref={wispRef}
          name="wall-mist-wisps"
          args={[wispGeo, wispMaterial, Math.max(anchors.length, 1)]}
          frustumCulled={false}
          raycast={() => null}
          renderOrder={7}
        />
      )}
    </group>
  );
}
